import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useDataProvider } from 'src/mtska-frontend-data-provider/useDataProvider';
import {
    DASHBOARD_REPORT_CONFIGURATION_DATA_PROVIDER,
    DASHBOARD_REPORT_CONFIGURATION_DOCUMENT_ID,
} from './dashboardReportConfigurationConstants';

let loadOneInFlight = null;

/**
 * @param {object} entity Riga in `global.items` (merge di `item.data` dopo GETONE) o equivalente.
 * @returns {object|null} Stessa forma della root di `configuration.json` (`report`, `filterSets`, `filters`).
 */
export const normalizeDashboardReportConfigurationPayload = (entity) => {
    if (!entity || typeof entity !== 'object') return null;
    if (entity.report && Array.isArray(entity.report.sections)) return entity;
    if (entity.configuration?.report && Array.isArray(entity.configuration.report.sections)) {
        return entity.configuration;
    }
    if (
        entity.dashboardConfiguration?.report &&
        Array.isArray(entity.dashboardConfiguration.report.sections)
    ) {
        return entity.dashboardConfiguration;
    }
    return null;
};

/**
 * Carica la configurazione via Redux (`bis/bi` + `loadOne('configuration')`); se è già in `global.items`, non rifetch.
 */
export const useDashboardReportConfiguration = () => {
    const { loadOne } = useDataProvider(DASHBOARD_REPORT_CONFIGURATION_DATA_PROVIDER);
    const loadOneRef = useRef(loadOne);
    loadOneRef.current = loadOne;

    const docId = DASHBOARD_REPORT_CONFIGURATION_DOCUMENT_ID;

    const storedRow = useSelector(
        (state) =>
            state[DASHBOARD_REPORT_CONFIGURATION_DATA_PROVIDER]?.global?.items?.[docId] ?? null,
    );

    const storedItem = useSelector(
        (state) => state[DASHBOARD_REPORT_CONFIGURATION_DATA_PROVIDER]?.global?.item ?? null,
    );

    const normalized = useMemo(() => {
        const candidates = [
            storedRow,
            storedItem?.data,
            storedItem,
        ];
        for (const candidate of candidates) {
            const normalizedCandidate = normalizeDashboardReportConfigurationPayload(candidate);
            if (normalizedCandidate) return normalizedCandidate;
        }
        return null;
    }, [storedRow, storedItem]);

    const reportConfiguration = normalized;

    useEffect(() => {
        if (storedRow) return;
        if (!loadOneInFlight) {
            loadOneInFlight = loadOneRef
                .current(docId)
                .catch(() => {})
                .finally(() => {
                    loadOneInFlight = null;
                });
        }
    }, [storedRow, docId]);

    return { reportConfiguration };
};
